import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import {
  PageContainer,
  ModalOverlay,
  Header,
  LoadingMessage,
  FilterButton,
  FilterContainer,
  FormGroup,
  ButtonGroup,
  CancelButton,
  ModalContent,
  ModalHeader,
  NoPostsMessage,
  PostCard,
  PostContent,
  PostList,
  PostMeta,
  PostTitle,
  ReplyButton,
  Select,
  SubmitButton,
  SuccessMessage,
  Textarea,  
  ErrorMessage,
  Label,
  Input
}from './styles/suppliers.styles'
// Define the Post type
interface Post {
  id: string;
  title: string;
  content: string;
  author: string;
  createdAt: any;
  category?: string;
}

// Define the Reply type - Updated to reflect the new options
interface ReplyFormData {
  postId: string;
  postTitle: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  message: string;
  // Updated type: 'whatsapp' for external contact, 'db_only' for internal submission
  replyMethod: 'whatsapp' | 'db_only';
}


const Suppliers: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'supplier'>('supplier');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [currentPost, setCurrentPost] = useState<Post | null>(null);
  // UPDATED: status to differentiate between database-only and whatsapp success
  const [replyStatus, setReplyStatus] = useState<'idle' | 'success_whatsapp' | 'success_db' | 'error'>('idle');
  const [submitting, setSubmitting] = useState(false);
  
  const [replyForm, setReplyForm] = useState<ReplyFormData>({
    postId: '',
    postTitle: '',
    userName: '',
    userEmail: '',
    userPhone: '',
    message: '',
    replyMethod: 'db_only' // CHANGED: Default is now internal submission
  });

  const db = getFirestore();

  const fetchPosts = async (filterType: 'all' | 'supplier' = 'supplier') => {
    try {
      setLoading(true);
      setError(null);

      let q;
      if (filterType === 'supplier') {
        // Query for supplier-specific posts
        q = query(
          collection(db, 'adminPosts'),
          where('category', '==', 'supplier'),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Query for all posts (fallback if category filter fails)
        q = query(
          collection(db, 'adminPosts'),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      
      const fetchedPosts: Post[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        fetchedPosts.push({ 
          id: doc.id, 
          title: data.title || 'Untitled',
          content: data.content || '',
          author: data.author || 'Unknown',
          createdAt: data.createdAt,
          category: data.category
        } as Post);
      });

      // If no posts found with supplier category, try getting all posts
      if (filterType === 'supplier' && fetchedPosts.length === 0) {
        console.log('No supplier posts found, fetching all posts...');
        await fetchPosts('all');
        return;
      }

      setPosts(fetchedPosts);
    } catch (err) {
      console.error("Error fetching posts:", err);
      
      // If the error is due to category index, try fetching all posts
      if (err instanceof Error && err.message.includes('index')) {
        console.log('Category index not ready, fetching all posts instead...');
        await fetchPosts('all');
        return;
      }
      
      setError("Failed to load posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts('supplier');
  }, [db]);

  const handleFilterChange = (newFilter: 'all' | 'supplier') => {
    setFilter(newFilter);
    fetchPosts(newFilter);
  };

  const handleReplyClick = (post: Post) => {
    setCurrentPost(post);
    setReplyForm({
      postId: post.id,
      postTitle: post.title,
      userName: '',
      userEmail: '',
      userPhone: '',
      message: '',
      replyMethod: 'db_only' // CHANGED: Set default to 'db_only'
    });
    setShowReplyModal(true);
    setReplyStatus('idle');
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setReplyStatus('idle');

    try {
      const whatsappNumber = '+256790807572'; 
      const repliesCollection = collection(db, 'replies'); // NEW: Target collection for replies

      // 1. Prepare and Save Data to Firestore (Always run this first)
      const replyDataToSave = {
        postId: replyForm.postId,
        postTitle: replyForm.postTitle,
        userName: replyForm.userName,
        userEmail: replyForm.userEmail,
        userPhone: replyForm.userPhone,
        message: replyForm.message,
        replyMethod: replyForm.replyMethod, 
        repliedAt: serverTimestamp(), // Use Firestore timestamp
        status: 'new', // Track new replies
      };

      await addDoc(repliesCollection, replyDataToSave); // Save to database

      // 2. Handle External Action (WhatsApp) or Internal Success Message
      if (replyForm.replyMethod === 'whatsapp') {
        const whatsappMessage = `*New Response to Post: ${replyForm.postTitle}*
          *From:* ${replyForm.userName}
          *Email:* ${replyForm.userEmail}
          *Phone:* ${replyForm.userPhone}
          *Message:*
          ${replyForm.message}
          *Post ID:* ${replyForm.postId}`;
        const encodedMessage = encodeURIComponent(whatsappMessage);
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
        
        // Open WhatsApp in new tab
        window.open(whatsappUrl, '_blank');
        
        setReplyStatus('success_whatsapp'); // Success status for WhatsApp
        
      } else {
        // 'db_only' submission success
        setReplyStatus('success_db'); // Success status for DB-only
      }

      // Close modal after a delay
      setTimeout(() => {
        setShowReplyModal(false);
        setReplyStatus('idle');
      }, 2500);

    } catch (err) {
      console.error('Error submitting reply:', err);
      setReplyStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ReplyFormData, value: string) => {
    setReplyForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(timestamp).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading announcements...</LoadingMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>Supplier Announcements</Header>
      
      <FilterContainer>
        <FilterButton 
          active={filter === 'supplier'} 
          onClick={() => handleFilterChange('supplier')}
        >
          Supplier Posts
        </FilterButton>
        <FilterButton 
          active={filter === 'all'} 
          onClick={() => handleFilterChange('all')}
        >
          All Posts
        </FilterButton>
      </FilterContainer>

      {error && (
        <ErrorMessage>
          {error}
          <div style={{ marginTop: '0.5rem' }}>
            <button 
              onClick={() => fetchPosts(filter)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </ErrorMessage>
      )}

      {posts.length > 0 ? (
        <PostList>
          {posts.map((post) => (
            <PostCard key={post.id}>
              <PostTitle>{post.title}</PostTitle>
              <PostContent>{post.content}</PostContent>
              {/* <PostMeta>
                <span>Posted by: {post.author}</span>
                <span>{formatDate(post.createdAt)}</span>
              </PostMeta> */}
              {/* {post.category && (
                <PostMeta>
                  <span>Category: {post.category}</span>
                </PostMeta>
              )} */}
              <ReplyButton onClick={() => handleReplyClick(post)}>
                Reply to this Post
              </ReplyButton>
            </PostCard>
          ))}
        </PostList>
      ) : (
        <NoPostsMessage>
          {filter === 'supplier' 
            ? "No supplier announcements found." 
            : "No announcements found."
          }
        </NoPostsMessage>
      )}

      {/* Reply Modal */}
      {showReplyModal && (
        <ModalOverlay onClick={() => !submitting && setShowReplyModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Reply to: {currentPost?.title}</ModalHeader>
            
            {/* Conditional Success Messages based on replyMethod */}
            {replyStatus === 'success_whatsapp' && (
              <SuccessMessage>
                Your response has been **saved to the database!** Redirecting you to WhatsApp to send the message...
              </SuccessMessage>
            )}

            {replyStatus === 'success_db' && (
              <SuccessMessage>
                **Feedback Submitted to Admin!** Thank you for your response.
              </SuccessMessage>
            )}

            {replyStatus === 'error' && (
              <ErrorMessage>
                Failed to submit your response. Please try again.
              </ErrorMessage>
            )}

            <form onSubmit={handleReplySubmit}>
              <FormGroup>
                <Label htmlFor="userName">Your Name *</Label>
                <Input
                  id="userName"
                  type="text"
                  value={replyForm.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  required
                  disabled={submitting}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="userEmail">Your Email</Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={replyForm.userEmail}
                  onChange={(e) => handleInputChange('userEmail', e.target.value)}
                  disabled={submitting}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="userPhone">Your Phone Number</Label>
                <Input
                  id="userPhone"
                  type="tel"
                  value={replyForm.userPhone}
                  onChange={(e) => handleInputChange('userPhone', e.target.value)}
                  disabled={submitting}
                  placeholder="+256 XXX XXX XXX"
                />
              </FormGroup>

              {/* MODIFIED: Select now only offers 'db_only' and 'whatsapp' */}
              <FormGroup>
                <Label htmlFor="replyMethod">Action *</Label>
                <Select
                  id="replyMethod"
                  value={replyForm.replyMethod}
                  onChange={(e) => handleInputChange('replyMethod', e.target.value as 'whatsapp' | 'db_only')}
                  disabled={submitting}
                >
                  <option value="db_only">Send to Grema Admin</option>
                  <option value="whatsapp">Send via WhatsApp</option>
                </Select>
              </FormGroup>
              {/* END OF MODIFIED SELECT */}

              <FormGroup>
                <Label htmlFor="message">Your Message *</Label>
                <Textarea
                  id="message"
                  value={replyForm.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  required
                  disabled={submitting}
                  placeholder="Type your response to this post..."
                />
              </FormGroup>

              <ButtonGroup>
                <CancelButton 
                  type="button" 
                  onClick={() => setShowReplyModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? 'Preparing...' : `Submit`}
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Suppliers;